import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, InputAdornment, ClickAwayListener, useTheme } from '@mui/material';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css'; // 引入默认样式，后面会被覆盖
import '../../styles/CustomDatePicker.css'; // 引入自定义样式
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { format } from 'date-fns';

/**
 * 自定义日期选择器组件
 * 
 * @param {Object} props
 * @param {string} props.label - 输入框标签
 * @param {Date|null} props.value - 当前选择的日期值
 * @param {function} props.onChange - 日期变更回调 (date) => void
 * @param {string} props.name - 输入框名称，用于表单
 * @param {boolean} props.fullWidth - 是否占满容器宽度
 * @param {string} props.size - 输入框大小，同MUI的size属性
 * @param {Object} props.inputProps - 传递给TextField的其他属性
 * @param {string} props.dateFormat - 日期显示格式，默认为'yyyy-MM-dd'
 * @param {Date|null} props.minDate - 最小可选日期
 * @param {Date|null} props.maxDate - 最大可选日期
 */
const CustomDatePicker = ({
  label = '选择日期',
  value = null,
  onChange,
  name,
  fullWidth = false,
  size = 'small',
  inputProps = {},
  dateFormat = 'yyyy-MM-dd',
  minDate = null,
  maxDate = null,
  ...props
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState('');
  const inputRef = useRef(null);

  // 更新显示值
  useEffect(() => {
    if (value) {
      setDisplayValue(format(new Date(value), dateFormat));
    } else {
      setDisplayValue('');
    }
  }, [value, dateFormat]);

  // 切换日历面板显示状态
  const toggleCalendar = () => {
    setOpen(!open);
  };

  // 关闭日历面板
  const handleClickAway = () => {
    setOpen(false);
  };

  // 处理日期选择
  const handleDateChange = (date) => {
    if (onChange) {
      onChange(date);
    }
    setOpen(false);
  };

  // 输入框自定义样式
  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '40px',
      height: '37px',
      fontSize: '0.875rem',
      fontWeight: 400,
      '& .MuiOutlinedInput-notchedOutline': {
        borderRadius: '40px',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      }
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.primary.main,
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    '& input': {
      fontWeight: 400,
      color: theme.palette.text.primary,
      fontSize: '0.875rem',
      fontFamily: theme.typography.fontFamily,
      padding: '8px 14px',
    },
  };

  // 日历容器样式
  const calendarContainerStyle = {
    position: 'absolute',
    zIndex: 1000,
    top: 'calc(100% + 8px)',
    left: 0,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    padding: '16px',
    width: '300px',
    animation: 'fadeIn 0.2s ease',
    '@keyframes fadeIn': {
      from: { 
        opacity: 0,
        transform: 'translateY(-10px)'
      },
      to: { 
        opacity: 1,
        transform: 'translateY(0)'
      }
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative', width: fullWidth ? '100%' : 'auto' }}>
        <TextField
          label={label}
          value={displayValue}
          name={name}
          ref={inputRef}
          onClick={toggleCalendar}
          fullWidth={fullWidth}
          size={size}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <CalendarTodayIcon 
                  fontSize="small" 
                  sx={{ 
                    color: theme.palette.primary.main,
                    cursor: 'pointer'
                  }}
                  onClick={toggleCalendar}
                />
              </InputAdornment>
            ),
            ...inputProps
          }}
          sx={{
            ...textFieldStyle,
            ...(props.sx || {})
          }}
        />
        
        {open && (
          <Box sx={calendarContainerStyle}>
            <Calendar
              onChange={handleDateChange}
              value={value ? new Date(value) : null}
              locale="zh-CN"
              formatShortWeekday={(locale, date) => 
                ['日', '一', '二', '三', '四', '五', '六'][date.getDay()]
              }
              formatDay={(locale, date) => date.getDate()}
              formatMonthYear={(locale, date) => 
                `${date.getFullYear()}年${date.getMonth() + 1}月`
              }
              nextLabel="›"
              prevLabel="‹"
              className="custom-calendar"
              minDate={minDate}
              maxDate={maxDate}
            />
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default CustomDatePicker; 